import { useMutation, useQuery } from "@tanstack/react-query";
import { whatsappService } from "../services/whatsapp";
import { handleMessages } from "../utils/handle-messages";

export const whatsappKeys = {
  all: ["whatsapp"] as const,
  cadastro: (dbName: string, ops: string) =>
    [...whatsappKeys.all, "cadastro", dbName, ops] as const,
};

export function useCadastroInfo(
  dbName: string,
  ops: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: whatsappKeys.cadastro(dbName, ops),
    queryFn: async () => {
      if (!dbName || !ops) return null;
      const response = await whatsappService.getCadastroInfo(
        dbName,
        ops,
        "all"
      );
      return response.data;
    },
    enabled: !!dbName && !!ops && enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

export function useGetAllConversationsMutation() {
  return useMutation({
    mutationFn: async ({
      clientName,
      ops,
      start,
      end,
    }: {
      clientName: string;
      ops: string;
      start: number;
      end: number;
    }) => {
      const messagesResponse = await whatsappService.getMessagesByTimestamp(
        clientName,
        ops,
        start,
        end
      );

      if (!messagesResponse.data || messagesResponse.data.length === 0) {
        throw new Error("Não existem mensagens no período selecionado");
      }

      const formatedMessages = handleMessages(
        messagesResponse.data,
        start,
        end
      );
      const conversations = Object.values(formatedMessages);

      await Promise.all(
        conversations.map(async (conversation) => {
          try {
            const [userStateResponse, userCursorResponse] = await Promise.all([
              whatsappService.getUserState(clientName, conversation.phone, ops),
              whatsappService.getUserCursor(
                clientName,
                conversation.phone,
                ops
              ),
            ]);

            conversation.userState = userStateResponse.data?.userState;
            conversation.cursor = userCursorResponse.data?.cursor;
          } catch (error) {
            console.error(
              `Error fetching state/cursor for ${conversation.phone}:`,
              error
            );
          }
        })
      );

      return conversations;
    },
  });
}

export function useGetSingleConversationMutation() {
  return useMutation({
    mutationFn: async ({
      clientName,
      phone,
      ops,
      start,
      end,
      dbName,
    }: {
      clientName: string;
      phone: string;
      ops: string;
      start: number;
      end: number;
      dbName: string;
    }) => {
      const messagesResponse =
        await whatsappService.getMessagesByPhoneAndTimestamp(
          clientName,
          phone,
          ops,
          start,
          end
        );

      if (!messagesResponse.data || messagesResponse.data.length === 0) {
        throw new Error("Não existem mensagens no período selecionado");
      }

      const formatedMessages = handleMessages(
        messagesResponse.data,
        start,
        end
      );
      let conversation = Object.values(formatedMessages)[0];

      const [userStateResponse, userCursorResponse, cadastroResponse] =
        await Promise.all([
          whatsappService.getUserState(clientName, phone, ops),
          whatsappService.getUserCursor(clientName, phone, ops),
          whatsappService.getCadastroInfo(dbName, ops, "individual", phone),
        ]);

      conversation.userState = userStateResponse.data?.userState;
      conversation.cursor = userCursorResponse.data?.cursor;

      if (
        cadastroResponse.data?.cadastro &&
        cadastroResponse.data.cadastro.length > 0
      ) {
        const cadastroData = cadastroResponse.data.cadastro[0];
        conversation = {
          ...conversation,
          name: cadastroData.name,
          cpf: cadastroData.cpf,
          email: cadastroData.email,
        };
      }

      return conversation;
    },
  });
}

export function useResetUserStateMutation() {
  return useMutation({
    mutationFn: async ({
      clientName,
      phone,
      ops,
    }: {
      clientName: string;
      phone: string;
      ops: string;
    }) => {
      const response = await whatsappService.resetUserState(
        clientName,
        phone,
        ops
      );
      return response.data;
    },
  });
}

export function useResetCursorMutation() {
  return useMutation({
    mutationFn: async ({
      clientName,
      phone,
      ops,
    }: {
      clientName: string;
      phone: string;
      ops: string;
    }) => {
      const response = await whatsappService.resetCursor(
        clientName,
        phone,
        ops
      );
      return response.data;
    },
  });
}
